"use client";

import posthog from "posthog-js";
import { PostHogProvider as PHProvider } from "posthog-js/react";
import { useEffect, useLayoutEffect } from "react";
import { usePathname } from "next/navigation";
import {
  readCookieConsent,
  readCookieValue,
} from "@/components/analytics/browser-attribution";
import { isSuppressedStaleDeployExceptionEvent } from "@/lib/chunk-load-recovery";
import {
  isAnonymousCrossOriginException,
  isBenignResizeObserverLoop,
  isThirdPartyTurnstileException,
} from "@/lib/posthog-exception-filter";
import { isSurveySurface } from "@/lib/analytics/survey-surface";
import { POSTHOG_BROWSER_INITIALIZED_EVENT } from "@/lib/analytics/posthog-browser-lifecycle";
import { COOKIE_CONSENT_NAME } from "@/lib/cookie-consent";
import { useAnalyticsSuppressed } from "@/components/analytics/use-analytics-suppressed";
import { isAnalyticsSuppressedUrl } from "@/lib/analytics/suppressed-paths";

// Whether the CURRENT browser URL is a bearer-token surface where analytics
// must not fire — /team/join/<token> (wrapped by the (app) layout's
// PostHogProvider) or /login|/signup carrying a token in ?redirect_url. Read at
// dispatch time so it's race-proof against auto-pageviews on SPA navigation.
function isCurrentUrlSuppressed(): boolean {
  if (typeof window === "undefined") return false;
  return isAnalyticsSuppressedUrl(
    window.location.pathname,
    window.location.search,
  );
}

// Importing posthog-js creates an uninitialised singleton. Do not call init at
// module scope: init creates an anonymous identity and can fetch remote config
// before React has reconciled the authoritative consent cookie. The singleton
// is initialised lazily, exactly once, after an accepted cookie is observed.
// Funnel experiments use a deterministic local fallback until that happens.
let postHogInitStarted = false;
let postHogLoadedEffectsStarted = false;

function ensurePostHogAnonCookie() {
  if (!posthog.__loaded || readCookieValue("ph_anon_id")) return;
  const distinctId = posthog.get_distinct_id();
  if (!distinctId) return;
  const expires = new Date(Date.now() + 365 * 864e5).toUTCString();
  const secure = window.location.protocol === "https:" ? "; Secure" : "";
  document.cookie = `ph_anon_id=${encodeURIComponent(distinctId)}; expires=${expires}; path=/; SameSite=Lax${secure}`;
}

function onPostHogLoaded() {
  // Consent may have been withdrawn while the SDK was loading. before_send is
  // the final event-dispatch barrier. The live URL check closes the equivalent
  // navigation race: init can begin on a normal page, then finish after an SPA
  // navigation to a bearer-token route whose suppression effect could not opt
  // out an SDK that was not loaded yet. Fail closed before enabling capture,
  // exception autocapture, or replay in either case.
  if (readCookieConsent() !== "accepted" || isCurrentUrlSuppressed()) {
    posthog.opt_out_capturing();
    posthog.stopSessionRecording();
    return;
  }

  // An asynchronous init may have completed while consent was withdrawn and
  // left the SDK opted out. Opt back in before announcing readiness so child
  // trackers cannot consume their retry/dedupe state on a silent no-op.
  posthog.opt_in_capturing();
  ensurePostHogAnonCookie();

  // Stamp every event with the git SHA of the BUNDLE that captured it
  // (BUILD_COMMIT_SHA is inlined at build time — see next.config.ts `env`).
  // Deploy-skew triage depends on this: a stale tab that throws
  // UnrecognizedActionError / ChunkLoadError reports the OLD build's SHA, so
  // an Error Tracking Issue can be read at a glance as "weeks-old tab, noise"
  // vs "current build, real". In the loaded callback so the initial
  // $pageview carries it, and deliberately ABOVE the one-time effects guard:
  // an opt-out round trip (consent withdrawal, or entering a suppressed
  // token URL) clears PostHog persistence — including super properties — so
  // every accepted (re-)entry must re-register (Codex P2, PR #1663).
  posthog.register({ app_build: process.env.BUILD_COMMIT_SHA || "dev" });

  if (postHogLoadedEffectsStarted) return;
  postHogLoadedEffectsStarted = true;

  // Error Tracking — enable exception autocapture so unhandled errors,
  // unhandled promise rejections, and console errors use PostHog's native
  // $exception payload. This is deliberately started only after consent.
  posthog.startExceptionAutocapture();
  scheduleSessionRecording();
  window.dispatchEvent(new Event(POSTHOG_BROWSER_INITIALIZED_EVENT));
}

function initPostHog(): boolean {
  if (typeof window === "undefined") return false;
  if (readCookieConsent() !== "accepted") return false;
  if (posthog.__loaded) {
    // Covers re-acceptance after consent was withdrawn while an asynchronous
    // init was in flight. The loaded hook is idempotent and will start the
    // deferred instrumentation/event only when consent is currently accepted.
    onPostHogLoaded();
    return true;
  }
  if (postHogInitStarted) return false;

  const key = process.env.NEXT_PUBLIC_POSTHOG_KEY;
  if (!key) return false;
  postHogInitStarted = true;

  // Phase 1 INFRA-12 — read ph_anon_id cookie (set by middleware.ts) so the
  // client PostHog instance boots with the SAME distinct_id the server-side
  // trackServerEvent() uses. This is what makes the anon→authed alias merge
  // work end-to-end across page loads + SSR events.
  const anonId = readCookieValue("ph_anon_id");

  try {
    posthog.init(key, {
      // Reverse proxy (health-check `reverse_proxy`): the browser SDK talks to
      // our OWN origin at `/ingest`, which next.config.ts rewrites forward to
      // PostHog US ingestion. This dodges ad-blockers that blocklist
      // `*.posthog.com` and would otherwise silently drop events + replays.
      // `ui_host` keeps the toolbar / "view in PostHog" deep-links pointing at
      // the real app. Deliberately NOT `NEXT_PUBLIC_POSTHOG_HOST` — that env var
      // stays the absolute host for the server-side SDK (lib/posthog.ts); the
      // browser must use the relative proxy path so requests stay same-origin.
      api_host: "/ingest",
      ui_host: "https://us.posthog.com",
      capture_pageview: true,
      autocapture: true,
      // Capture silent no-op clicks, including the detached-label regression
      // class fixed in PR #1665. The SDK's boolean form also enables dead-swipe
      // events, so keep those out of this deliberately click-only rollout.
      // before_send applies the existing consent and token-URL dispatch gates.
      capture_dead_clicks: { capture_dead_swipes: false },
      session_recording: {
        recordCrossOriginIframes: true,
      },
      // Recording is started lazily below (see scheduleSessionRecording) so
      // rrweb's snapshot + DOM instrumentation does NOT run synchronously during
      // hydration — that work was a measured contributor to main-thread blocking
      // (Total Blocking Time) that delayed first input on slower mobile devices.
      // Auto-start is disabled here; startSessionRecording() is called once the
      // page is idle or on the first interaction. Recording still happens for
      // consented users — it just starts a beat later, and rrweb's full snapshot
      // at start captures the interactive session intact.
      disable_session_recording: true,
      // surveys.js (~32 KB via /ingest) only powers native popover surveys for
      // signed-in product users; NPS + cancellation are mirrored server-side.
      // Keep the extension off the anonymous marketing/ads/funnel/auth surfaces
      // where it loaded 81%-unused inside the mobile LCP window. See
      // lib/analytics/survey-surface.ts for the full rationale + guard test.
      disable_surveys: !isSurveySurface(window.location.pathname),
      // init itself is deferred until the authoritative cookie is accepted, so
      // no opt-out marker, anonymous UUID or remote-config request is created for
      // an undecided/declined visitor.
      opt_out_capturing_by_default: false,
      // Once an accepted visitor withdraws, opt_out_capturing() must also clear
      // PostHog's identity/session persistence from cookies and localStorage.
      // The consent cookie remains our sole durable authority.
      opt_out_persistence_by_default: true,
      // Phase 1 INFRA-12: bootstrap distinct_id from server-issued cookie.
      bootstrap: anonId ? { distinctID: anonId } : undefined,
      // The PostHog project has `defaultIdentifiedOnly: true` (visible in the
      // remote config at /array/{token}/config.js). That tells the SDK to skip
      // feature-flag evaluation for anonymous users because no person profile
      // is created for them. On authed routes, Clerk triggers posthog.identify()
      // and unlocks flag eval. On (marketing) routes there's no identify call,
      // so without this override the `funnel-v2` A/B test would never resolve
      // for anonymous homepage visitors. `person_profiles: "always"` forces
      // PostHog to create a profile even for anonymous users, so flag eval
      // and capture both work on marketing pages.
      person_profiles: "always",
      // before_send drops two classes of unactionable $exception noise:
      //
      //  1. Stale-deploy skew (lib/chunk-load-recovery.ts): the transient
      //     ChunkLoadError / UnrecognizedActionError that fires when an
      //     already-open tab requests a chunk or Server Action ID a NEW deploy
      //     invalidated — the error boundaries and the root layout's
      //     <StaleDeployRejectionRecovery /> auto-heal both with a guarded
      //     reload, so reporting them would just spray a deploy-time Issue per
      //     release (PostHog Error Tracking issues 2026-06-12 + 019f8e03).
      //     Conditional: once a reload attempt is recorded in sessionStorage,
      //     recurrences pass through and DO form Issues — an artifact still
      //     missing after a fresh reload is a real outage.
      //  2. Anonymized cross-origin "Script error." (lib/posthog-exception-filter.ts):
      //     when a third-party script (Cloudflare Turnstile, Clerk, Stripe, GTM,
      //     Clarity, Meta Pixel) throws, the browser redacts the event to a bare
      //     "Script error." with no message or stack. PostHog can't fingerprint
      //     it, so every hit collapses into one phantom "(unknown error)" Issue
      //     (GitHub #795). Dropped only when there is genuinely nothing to act on
      //     (empty/"Script error." value AND no stack on every entry); any
      //     same-origin app exception carries a stack and passes through.
      //  3. Benign "ResizeObserver loop …" notice (lib/posthog-exception-filter.ts):
      //     a non-error the browser emits when a ResizeObserver callback reflows
      //     layout faster than notifications deliver in one frame. No exception
      //     propagates and no flow breaks, but browsers surface it via
      //     window.onerror so autocapture files it as an Issue — pure noise.
      //  4. Stack-bearing Cloudflare Turnstile client errors
      //     (lib/posthog-exception-filter.ts): the site-entrance gate and lead
      //     forms load Cloudflare Turnstile, whose generic challenge-execution
      //     failures (e.g. "TurnstileError: [Cloudflare Turnstile] Error:
      //     300030") throw WITH a stack pointing into Cloudflare's own bundle,
      //     so the case-2 stackless filter misses them. They are transient
      //     third-party client errors we cannot act on from our code. Dropped
      //     when an entry is unmistakably Turnstile's own (TurnstileError type,
      //     [Cloudflare Turnstile] message, or a Cloudflare-bundle stack frame).
      before_send: (event) =>
        // Re-read at the final SDK dispatch boundary. opt-out state and consent
        // listeners remain useful backstops, but this prevents any direct
        // capture/identify call racing a same-session or cross-tab withdrawal.
        readCookieConsent() !== "accepted" ||
        // Audit P1: drop every event fired while on a token-bearing URL — an
        // auto $pageview / autocapture on SPA navigation into /team/join/<token>
        // or /login?redirect_url=%2Fsite%2F<token> would otherwise send the full
        // token URL to PostHog. Race-proof: evaluated at dispatch against the
        // live location, so it catches the pageview regardless of effect timing.
        isCurrentUrlSuppressed() ||
        isSuppressedStaleDeployExceptionEvent(event) ||
        isAnonymousCrossOriginException(event) ||
        isBenignResizeObserverLoop(event) ||
        isThirdPartyTurnstileException(event)
          ? null
          : event,
      loaded: onPostHogLoaded,
    });
  } catch {
    // A transient SDK failure must not break the page. Allow one later consent
    // event to retry; before_send still fails closed if the partial init emits.
    postHogInitStarted = false;
    return false;
  }

  // posthog-js currently invokes loaded synchronously. Keep this idempotent
  // fallback for test doubles and future SDK versions with different timing.
  if (posthog.__loaded) onPostHogLoaded();
  return true;
}

// The SDK is only initialised after acceptance. This second guard handles a
// later withdrawal before a scheduled recording start. Safe to call more than
// once; posthog no-ops if already recording.
function startSessionRecordingIfAllowed() {
  if (typeof window === "undefined" || !posthog.__loaded) return;
  if (posthog.has_opted_out_capturing()) return;
  posthog.startSessionRecording();
}

// Defer recording off the synchronous hydration path: start on the first user
// interaction OR when the main thread goes idle, whichever fires first. Binding
// to the first interaction means any session worth reviewing (the user actually
// did something — clicks, typing, rage-clicks) records from that first action.
function scheduleSessionRecording() {
  if (typeof window === "undefined") return;
  let started = false;
  const startOnce = () => {
    if (started) return;
    started = true;
    window.removeEventListener("pointerdown", startOnce, true);
    window.removeEventListener("keydown", startOnce, true);
    startSessionRecordingIfAllowed();
  };
  window.addEventListener("pointerdown", startOnce, {
    capture: true,
    passive: true,
  });
  window.addEventListener("keydown", startOnce, {
    capture: true,
    passive: true,
  });
  if (typeof window.requestIdleCallback === "function") {
    window.requestIdleCallback(startOnce, { timeout: 3000 });
  } else {
    window.setTimeout(startOnce, 1500);
  }
}

export function PostHogProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const suppressed = useAnalyticsSuppressed(pathname);

  // Client-side navigation into a token-bearing URL must actively stop an
  // already-initialised PostHog: the auto $pageview and session replay would
  // otherwise carry the token. before_send is the race-proof backstop that
  // drops any event fired on a suppressed URL; this opts the SDK out and halts
  // replay on entry, and re-enables on return to a normal route (if consent is
  // still accepted). Runs before paint so replay stops promptly. (Audit P1.)
  useLayoutEffect(() => {
    if (suppressed) {
      // Only act if it was actually loaded on a prior page; if init was skipped
      // (first landing on a token route) there's nothing to opt out of.
      if (posthog.__loaded) {
        posthog.opt_out_capturing();
        posthog.stopSessionRecording();
      }
      return;
    }
    // Leaving a suppressed route (or on any normal route): ensure PostHog is
    // running when consent is accepted. initPostHog() is idempotent — it LOADS
    // the SDK if a first landing on a token route skipped init, and opts a
    // previously-loaded-then-opted-out instance back in (via onPostHogLoaded).
    // Without this, a session that started on /team/join/<token> would keep
    // PostHog off for the rest of the session after the invite is accepted.
    if (readCookieConsent() === "accepted") {
      if (initPostHog()) startSessionRecordingIfAllowed();
    }
  }, [suppressed]);

  // Surveys were disabled at init when the page loaded on an anonymous
  // surface (see disable_surveys above). The KNOWN client-side entry into the
  // authed app (/claim-callback → router.replace("/projects/<id>?welcome=1"))
  // is handled at init by putting /claim-callback on the survey surface; this
  // effect is the backstop for any other SPA navigation onto a survey
  // surface. Flipping the config alone is not enough: if the remote config
  // arrived while disable_surveys was true, the surveys manager never seeded
  // its enabled-state and loadIfEnabled() no-ops (posthog-js 1.390.2, Codex
  // review on PR #1276). reloadFeatureFlags() re-delivers the flags/remote
  // config, which re-runs the surveys manager's onRemoteConfig — now with
  // surveys enabled — seeding the state and loading the extension.
  // One-directional: once enabled it stays enabled for the session, same as
  // a hard load on an app path.
  useEffect(() => {
    if (!posthog.__loaded) return;
    if (!posthog.config.disable_surveys) return;
    if (!isSurveySurface(pathname ?? "")) return;
    posthog.set_config({ disable_surveys: false });
    // Covers the config-already-cached case; harmless no-op otherwise.
    posthog.surveys.loadIfEnabled();
    // Re-delivers remote config to seed the manager when the cached delivery
    // was swallowed by the disabled-at-init early return.
    posthog.reloadFeatureFlags();
  }, [pathname]);

  // Reconcile initial consent after the client commit but before descendant
  // passive effects can call PostHog, then listen for same-tab and cross-tab
  // changes without remounting the application tree.
  useLayoutEffect(() => {
    function applyConsent(accepted: boolean) {
      if (accepted) {
        if (isCurrentUrlSuppressed()) {
          // Token-bearing URL: never init/capture. If PostHog was already
          // loaded on a prior page (SPA navigation in), opt out + stop replay so
          // the token URL isn't sent as a $pageview or recorded in a session.
          if (posthog.__loaded) {
            posthog.opt_out_capturing();
            posthog.stopSessionRecording();
          }
          return;
        }
        if (!initPostHog() || !posthog.__loaded) return;
        // initPostHog()/onPostHogLoaded opts in and stitches the anonymous
        // identity before emitting the browser-ready lifecycle event.
        // Auto-start is disabled (see initPostHog); now that the user has
        // consented, start recording if it isn't already running.
        startSessionRecordingIfAllowed();
      } else {
        // Calling opt_out on an uninitialised singleton can itself persist an
        // SDK consent marker. Before first acceptance, leave PostHog entirely
        // untouched and only remove our own optional identity cookie.
        if (posthog.__loaded) {
          posthog.opt_out_capturing();
          posthog.stopSessionRecording();
        }
        document.cookie = "ph_anon_id=; Max-Age=0; path=/; SameSite=Lax";
      }
    }

    function onConsentStorage(event: StorageEvent) {
      if (event.key === COOKIE_CONSENT_NAME) onConsentChange(event);
    }

    function onConsentChange(_event: Event) {
      // The event is only a prompt to re-read state. Duplicate cookies from
      // different paths must fail closed rather than trusting its detail.
      applyConsent(readCookieConsent() === "accepted");
    }

    // Reconcile after hydration as CookieBanner may have migrated a legacy
    // preference from localStorage to the authoritative cookie during render.
    applyConsent(readCookieConsent() === "accepted");
    window.addEventListener("cookie_consent_change", onConsentChange);
    window.addEventListener("storage", onConsentStorage);
    return () => {
      window.removeEventListener("cookie_consent_change", onConsentChange);
      window.removeEventListener("storage", onConsentStorage);
    };
  }, []);

  // Always render PHProvider — never conditionally swap tree structure.
  // This prevents form state loss when cookie consent changes.
  return <PHProvider client={posthog}>{children}</PHProvider>;
}
